import { injectable } from 'inversify';

import { CategoriesEvaluations } from '@shared/models/assessmentdetails';
import { AdditionalCriteriaType, ClinicalCriteriaDTO } from '@shared/models/clinicalcriteria';
import { EvaluationsDTO } from '@shared/models/evaluations';
import { IAssessmentMatrix } from '@shared/models/matrix';
import { Domain } from '@shared/models/assessmentdetails';
import { BrainFunctionEvaluation } from '@shared/models/assessmentdetails';
import {
  AssessmentNoteDTO,
  BrainFunctionBatchHistory,
  ClinicalCriteriaBatchHistory,
} from '@shared/models/assesmentnotes';

export interface IAssessmentModelFactory {
  createBrainFunctionEvaluationModel: (
    matrix: IAssessmentMatrix,
    evaluations: EvaluationsDTO[],
    batchHistory: BrainFunctionBatchHistory[],
  ) => CategoriesEvaluations[];
  createAdditionalCriteriasEvaluationModel: (
    clinicalCriteria: ClinicalCriteriaDTO[],
    additionalCriteria: AdditionalCriteriaType[],
    batchHistory: ClinicalCriteriaBatchHistory[],
  ) => AdditionalCriteriaType[];
}

@injectable()
export class AssessmentModelFactory implements IAssessmentModelFactory {
  static diToken = Symbol('assessments-model-factory');

  getEvaluationRating = (assessments, brainDomain, levelId, brainFunctionId) => {
    const domain = assessments.find(item => item.brainDomain === brainDomain);
    if (!domain) {
      return null;
    }

    const levelEvaluation = domain.levelEvaluations.find(item => item.levelId === levelId);
    if (!levelEvaluation) {
      return null;
    }

    const functionsEvaluation = levelEvaluation.functionsEvaluations.find(
      item => item.brainFunctionId === brainFunctionId,
    );
    const functionsEvaluationHistory = levelEvaluation.functionsEvaluationsHistory.find(item => {
      return (
        item.brainFunctionId === brainFunctionId &&
        item.evaluationRating !== functionsEvaluation.evaluationRating
      );
    });

    if (!functionsEvaluation) {
      return null;
    }

    return {
      functionsEvaluation,
      functionsEvaluationHistory,
    };
  };

  createBrainFunctionEvaluationModel = (
    matrix,
    evaluations,
    batchHistory: BrainFunctionBatchHistory[],
  ) => {
    const tabs: CategoriesEvaluations[] = [1, 2, 3, 4, 5, 6, 7, 8].map(index => {
      const categoryNamesDuplicate: string[] = [];
      const domainNamesDuplicate: string[] = [];

      const brainFunctionsByLevel = matrix.brainFunctions.filter(item => {
        categoryNamesDuplicate.push(item.category);
        domainNamesDuplicate.push(item.domain);

        return item.level.brainLevelId === index;
      });

      const categoryNames = Array.from(new Set(categoryNamesDuplicate));
      const domainNames = Array.from(new Set(domainNamesDuplicate));

      const categoriesItem = {};

      categoryNames.forEach(category => {
        const domains = {};

        const section = brainFunctionsByLevel
          .filter(item => item.category === category)
          .map(item => {
            const rating = this.getEvaluationRating(
              evaluations,
              item.domain,
              item.level.brainLevelId,
              item.id,
            );

            const brainFunctionHistory = batchHistory.find(h => h.brainFunctionId === item.id);
            const notes = brainFunctionHistory?.notes ?? [];

            const latestNote: AssessmentNoteDTO | null =
              notes
                .filter(note => !note.isDeleted)
                .sort(
                  (a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime(),
                )?.[0] ?? null;

            return {
              ...item,
              evaluation: rating?.functionsEvaluation,
              evaluationHistory: rating?.functionsEvaluationHistory,
              note: latestNote?.text,
              noteAuthor: latestNote?.author?.fullname,
              noteDeleted: latestNote?.isDeleted,
              noteDate: latestNote?.creationDate,
              isFlagChanged: brainFunctionHistory?.isFlagChanged ?? false,
            };
          });

        domainNames.forEach(domain => {
          const subSection = section.filter(item => item.domain === domain);
          const levelId = subSection[0]?.level?.brainLevelId;

          Object.assign(domains, {
            [domain]: new Domain(
              subSection,
              this.getMarkedAsNotYetAchieved(evaluations, domain, levelId),
              this.getDomainTitle(subSection),
            ),
          });

          Object.assign(categoriesItem, { [category]: domains });
        });
      });

      return categoriesItem;
    });

    return tabs;
  };

  createAdditionalCriteriasEvaluationModel = (
    clinicalCriteria,
    additionalCriteria,
    batchHistory: ClinicalCriteriaBatchHistory[],
  ) => {
    const result = clinicalCriteria.map(item => {
      const clinicalCriteriaHistory = batchHistory.find(
        h => h.clinicalCriterionId === item.clinicalCriterionId,
      );
      const notes = clinicalCriteriaHistory?.notes ?? [];

      const latestNote: AssessmentNoteDTO | null =
        notes
          .filter(note => !note.isDeleted)
          .sort(
            (a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime(),
          )?.[0] ?? null;

      const additionalCriteriaItem = additionalCriteria.find(
        ac => ac.clinicalCriterionId === item.clinicalCriterionId,
      );

      const criteria = {
        title: item.title,
        clinicalCriterionId: item.clinicalCriterionId,
        options: item.options,
        option: {},
        note: latestNote?.text,
        noteAuthor: latestNote?.author?.fullname,
        noteDate: latestNote?.creationDate,
        noteDeleted: latestNote?.isDeleted,
        reviewStatus: additionalCriteriaItem?.reviewStatus,
        historicalClinicalCriterionOptionId:
          additionalCriteriaItem?.historicalClinicalCriterionOptionId,
        author: additionalCriteriaItem?.author,
        lastModified: additionalCriteriaItem?.lastModified,
        prevAssessmentNotesExist: additionalCriteriaItem?.prevAssessmentNotesExist ?? false,
        isFlagged: additionalCriteriaItem?.isFlagged ?? false,
        isFlagChanged: clinicalCriteriaHistory?.isFlagChanged,
      };

      item.options.forEach(option => {
        const additionalCriterion = additionalCriteria.find(
          (i: any) => i.clinicalCriterionOptionId === option.clinicalCriterionOptionID,
        );

        if (additionalCriterion) {
          const opt = { ...option };

          const prevOption = item.options.find(
            o => o.clinicalCriterionOptionID === additionalCriterion.prevOptionId,
          );

          if (prevOption) {
            Object.assign(opt, { prevOptionTitle: prevOption.title });
          }

          Object.assign(criteria, { option: opt });
        }
      });

      return criteria;
    });

    return result;
  };

  getMarkedAsNotYetAchieved = (
    evaluations: EvaluationsDTO[],
    brainDomain: string,
    levelId: number,
  ) => {
    const domain = evaluations.find(item => item.brainDomain === brainDomain);
    if (!domain) {
      return false;
    }

    const evaluation = domain.levelEvaluations.find(item => item.levelId === levelId);
    if (!evaluation) {
      return false;
    }

    return evaluation.markedAsNotYetAchievedBulk;
  };

  getDomainTitle = (subSection: BrainFunctionEvaluation[]) => {
    if (subSection.length) {
      return subSection[0].domainDescription;
    }

    return '';
  };
}
